import React, { useEffect, useRef, useState } from 'react';
import { useClient } from '@/context/client-context';
import { getClientPostureAnalyses } from '@/components/posture-analysis/services/posture-analyses-api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { formatDateOnlyLocale, sortRecordsBySortDateDesc } from '@/lib/utils';
import logger from '@/lib/logging';

interface AnalysisSelectorProps {
  onSelect: (analysis: any) => void;
  selectedId?: string;
  /**
   * Fires whenever this client's history (re)loads — lets the parent reuse
   * the same fetch for the "Day 1 vs latest" preset (ComparisonView, Phase
   * 4.2) instead of fetching it again. Client-context only (one client's
   * history, already fetched here — not a roster-wide array).
   */
  onAnalysesLoaded?: (analyses: any[]) => void;
}

const AnalysisSelector: React.FC<AnalysisSelectorProps> = ({ onSelect, selectedId, onAnalysesLoaded }) => {
  const { client } = useClient();
  const { user, loading: authLoading } = useAuth();
  const [analyses, setAnalyses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Ref (not a dependency) so an inline parent callback can't retrigger the fetch effect.
  const onAnalysesLoadedRef = useRef(onAnalysesLoaded);
  onAnalysesLoadedRef.current = onAnalysesLoaded;

  useEffect(() => {
    const fetchAnalyses = async () => {
      if (!client?.id) {
        setAnalyses([]);
        return;
      }

      if (authLoading || !user) {
        setIsLoading(false);
        return;
      }

      // Clear analyses immediately when client changes
      setAnalyses([]);
      setIsLoading(true);

      const currentClientId = client.id;

      try {
        logger.debug('AnalysisSelector: Fetching posture analyses for client:', currentClientId);
        const data = await getClientPostureAnalyses(currentClientId);

        const validAnalyses = data.filter((a) => {
          const analysisClientId = (a as any).clientId;
          if (analysisClientId && analysisClientId !== currentClientId) {
            logger.warn('AnalysisSelector: Found analysis with mismatched clientId:', {
              analysisId: a.id,
              expectedClientId: currentClientId,
              actualClientId: analysisClientId,
            });
            return false;
          }
          return true;
        });

        logger.debug(
          `AnalysisSelector: Fetched ${data.length} analyses, ${validAnalyses.length} valid for client ${currentClientId}`
        );

        if (client?.id === currentClientId) {
          const sorted = sortRecordsBySortDateDesc(validAnalyses);
          setAnalyses(sorted);
          onAnalysesLoadedRef.current?.(sorted);
        } else {
          logger.warn('AnalysisSelector: Client changed during fetch, discarding results');
        }
      } catch (err) {
        logger.error('Failed to load history', err);
        if (client?.id === currentClientId) {
          setAnalyses([]);
          onAnalysesLoadedRef.current?.([]);
        }
      } finally {
        if (client?.id === currentClientId) {
          setIsLoading(false);
        }
      }
    };
    fetchAnalyses();
  }, [client?.id, authLoading, user]);

  if (isLoading) return <div className="text-sm p-4">Loading history...</div>;
  if (analyses.length === 0) return <div className="text-sm p-4">No previous analyses found.</div>;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">History</CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        <ScrollArea className="h-[300px]">
          <div className="flex flex-col gap-2">
            {analyses.map((analysis) => (
              <Button
                key={analysis.id}
                variant={selectedId === analysis.id ? 'default' : 'ghost'}
                className="justify-start h-auto py-3 px-4"
                onClick={() => onSelect(analysis)}
              >
                <Calendar className="mr-2 h-4 w-4 shrink-0" />
                <div className="flex flex-col items-start text-left overflow-hidden">
                  <span className="font-medium text-xs truncate">
                    {formatDateOnlyLocale(analysis.createdAt)}
                  </span>
                  <span className="text-[10px] text-muted-foreground truncate w-full">
                    {analysis.classification?.pattern || 'Neutral'}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default AnalysisSelector;
